import moment from 'moment';
import React, { ComponentType } from 'react';
import Card from 'react-bootstrap/Card';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { generalErrorHandler } from '../../../actions/utils';
import LoadingImage from '../../../components/general/LoadingImage';
import { eligibleForHKEXProfile } from '../../../helpers/stockCode.helper';
import { validateDateStr } from '../../../helpers/ui.helper';
import {
  CCASSQueryDetailsResponse,
  CCASSSummary,
} from '../../../py-generated/datalouder/ccass/types';
import { RootState } from '../../../reducers';
import { ccassService } from '../../../services';
import { ReduxProps } from '../../../types';
import CCASSDetails from './CCASSDetails';
import CCASSSummaryTables from './CCASSSummaryTables';

interface Props {
  stockCode: string;
  date?: string;
  compareDate?: string;
  compareDatePicker?: (id: string) => React.JSX.Element;
  datePicker?: () => React.JSX.Element;
}

interface States {
  queryDataSummary: CCASSSummary;
  compareDataSummary?: CCASSSummary;
  participants: CCASSQueryDetailsResponse['participants'];
  loading: boolean;
}

class CCASSTables extends React.PureComponent<
  Props & ReduxProps<typeof mapStateToProps> & WithTranslation,
  States
> {
  constructor(props) {
    super(props);

    this.state = {
      queryDataSummary: undefined,
      compareDataSummary: undefined,
      participants: undefined,
      loading: false,
    };
  }

  fetchCCASS = () => {
    const { date, compareDate, stockCode, user, dispatch, t } = this.props;

    if (
      !stockCode ||
      !eligibleForHKEXProfile(stockCode) ||
      !user ||
      !user.hasMembership
    ) {
      return;
    }

    const queryDateStr = moment(date).format('YYYYMMDD');
    const compareDateStr = compareDate
      ? moment(compareDate).format('YYYYMMDD')
      : undefined;

    if (!validateDateStr(queryDateStr)) {
      window.alert(t('invalidDate') + ': ' + queryDateStr);
      return;
    }

    if (compareDateStr && !validateDateStr(compareDateStr)) {
      window.alert(t('invalidDate') + ': ' + compareDateStr);
      return;
    }

    this.setState({ loading: true });

    ccassService
      .query(stockCode, queryDateStr, compareDateStr)
      .then(({ queryData, compareData }) => {
        this.setState({
          queryDataSummary: queryData,
          compareDataSummary: compareData,
          loading: false,
        });
      })
      .catch(
        generalErrorHandler(dispatch, () => this.setState({ loading: false }))
      );
  };

  componentDidMount() {
    this.fetchCCASS();
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.props.stockCode !== prevProps.stockCode ||
      this.props.date !== prevProps.date ||
      this.props.compareDate !== prevProps.compareDate
    ) {
      this.fetchCCASS();
    }
  }

  render() {
    const { t, date, compareDate, compareDatePicker, datePicker } = this.props;
    const { loading, queryDataSummary, compareDataSummary } = this.state;
    return (
      <>
        <LoadingImage displayAsBlock={true} loading={loading} />
        {queryDataSummary === null && (
          <div className="text-center">{t('No data is found.')}</div>
        )}

        {queryDataSummary && (
          <Card>
            <Card.Header>{t('Summary')}</Card.Header>
            <Card.Body>
              <CCASSSummaryTables
                queryData={queryDataSummary}
                compareData={compareDataSummary}
              />{' '}
            </Card.Body>
          </Card>
        )}

        {queryDataSummary && (
          <Card style={{ marginTop: '1rem' }}>
            <Card.Header>
              {t(
                'Details of Market Intermediaries/Consenting Investor Participants'
              )}
            </Card.Header>
            <Card.Body>
              <CCASSDetails
                key={queryDataSummary.ticker + '/' + queryDataSummary.date}
                queryDataSummary={queryDataSummary}
                compareDataSummary={compareDataSummary}
                date={date}
                compareDate={compareDate}
                compareDatePicker={compareDatePicker}
                datePicker={datePicker}
              />
            </Card.Body>
          </Card>
        )}
      </>
    );
  }
}

function mapStateToProps(state: RootState) {
  const { user } = state.user;

  return {
    user,
  };
}

export default withTranslation()(
  connect(mapStateToProps)(CCASSTables)
) as ComponentType<Props>;
